
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useBucketManagement } from "@/hooks/storage/useBucketManagement";
import { BUCKET_NAME, CUSTOM_FOLDER } from "@/hooks/storage/constants";
import { useAvatarManager } from "@/hooks/useAvatarSync";

import { Profile, validateProfile, processAvatarUrl } from "@/types/profile";

export function useAvatarUpload(profile: Profile | null) {
  const [uploading, setUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const { updateProfile, fetchProfile, user } = useAuth();
  const { initializeBucket } = useBucketManagement();
  const { updateAvatar: updateGlobalAvatar } = useAvatarManager();

  // Determine if the avatar is custom (not a system avatar)
  useEffect(() => {
    console.log('Avatar effect triggered:', { profile, currentAvatarUrl: avatarUrl });

    const profileAvatarUrl = profile?.avatar_url;

    // Use type-safe processing instead of manual type guards
    if (profileAvatarUrl) {
      const processedAvatar = processAvatarUrl(profileAvatarUrl);

      if (processedAvatar.isSystemAvatar) {
        console.log('Profile has system avatar, ignoring');
        setAvatarUrl(null);
      } else if (processedAvatar.processedUrl) {
        console.log('Setting custom avatar URL:', processedAvatar.processedUrl);
        setAvatarUrl(processedAvatar.processedUrl);
      } else {
        console.log('Invalid avatar URL format');
        setAvatarUrl(null);
      }
    } else {
      console.log('No avatar URL in profile');
      setAvatarUrl(null);
    }
  }, [profile?.avatar_url]); // Only depend on avatar_url to avoid unnecessary re-runs

  async function uploadAvatar(event: React.ChangeEvent<HTMLInputElement>) {
    try {
      setUploading(true);

      if (!event.target.files || event.target.files.length === 0) {
        throw new Error("Você precisa selecionar uma imagem para fazer upload.");
      }

      if (!user?.id) {
        throw new Error("Usuário não está logado.");
      }

      // Verificar autenticação atual antes de prosseguir
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.user) {
        console.error("Erro de sessão:", sessionError);
        throw new Error("Sessão expirada. Por favor, faça login novamente.");
      }

      console.log("Upload iniciado com usuário:", session.user.id);

      // Garantir que o bucket existe antes de fazer upload
      const initResult = await initializeBucket(BUCKET_NAME);
      if (!initResult.success) {
        console.error("Falha na inicialização do bucket:", initResult.error);

        const errorMessage = initResult.error ?
          (typeof initResult.error === 'object' && 'message' in initResult.error ?
            initResult.error.message :
            JSON.stringify(initResult.error)) :
          "Erro desconhecido";

        throw new Error(`Não foi possível inicializar o bucket: ${errorMessage}`);
      }
      
      // Verificar se o perfil existe e criar se necessário
      let userProfile = profile;
      if (!userProfile) {
        userProfile = await fetchProfile(user.id);
        if (!userProfile) {
          throw new Error("Não foi possível obter ou criar o perfil do usuário.");
        }
      }

      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      
      // CRÍTICO: Usar timestamp no nome do arquivo para garantir unicidade e evitar cache
      // Isso força o CDN do Supabase a servir a nova versão
      const timestamp = Date.now();
      const randomSuffix = Math.random().toString(36).substring(2, 15);
      const fileName = `${user.id}-${timestamp}-${randomSuffix}.${fileExt}`;
      const filePath = `${CUSTOM_FOLDER}/${fileName}`;

      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        throw new Error("O arquivo deve ter no máximo 2MB.");
      }

      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error("O arquivo deve ser uma imagem (JPG, PNG ou GIF).");
      }

      // CRÍTICO: Deletar avatar antigo antes de fazer upload do novo
      // Isso evita que o CDN sirva a versão antiga do arquivo
      if (userProfile?.avatar_url) {
        const oldAvatarUrl = userProfile.avatar_url;
        const oldUrlParts = oldAvatarUrl.split('profiles/');
        if (oldUrlParts.length > 1) {
          const oldFilePath = oldUrlParts[1]?.split('?')[0];
          if (oldFilePath && oldFilePath.startsWith(CUSTOM_FOLDER)) {
            console.log("Deletando avatar antigo:", oldFilePath);
            try {
              const { error: deleteError } = await supabase.storage
                .from(BUCKET_NAME)
                .remove([oldFilePath]);
              
              if (deleteError) {
                console.warn("Aviso: Não foi possível deletar avatar antigo:", deleteError);
                // Continua mesmo se não conseguir deletar
              } else {
                console.log("Avatar antigo deletado com sucesso");
              }
            } catch (deleteErr) {
              console.warn("Erro ao deletar avatar antigo:", deleteErr);
              // Continua mesmo se não conseguir deletar
            }
          }
        }
      }

      console.log("Iniciando upload para:", filePath);
      console.log("Tipo do arquivo:", file.type);
      console.log("Tamanho do arquivo:", file.size);

      // Fazer o upload do arquivo para a pasta 'custom' no bucket 'profiles'
      // IMPORTANTE: Não usar upsert, sempre criar novo arquivo com nome único
      const { error: uploadError, data } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(filePath, file, {
          cacheControl: 'no-cache, no-store, must-revalidate',
          upsert: false, // Sempre criar novo arquivo
          contentType: file.type
        });

      if (uploadError) {
        console.error("Erro de upload:", uploadError);
        throw uploadError;
      }

      console.log("Upload concluído com sucesso!");

      // Obtenha a URL pública do arquivo
      const { data: urlData } = supabase.storage.from(BUCKET_NAME).getPublicUrl(filePath);
      
      if (!urlData || !urlData.publicUrl) {
        throw new Error("Não foi possível obter a URL pública da imagem.");
      }
      
      console.log("URL pública obtida:", urlData.publicUrl);
      
      // CRÍTICO: Remove qualquer cache-busting existente da URL antes de salvar no banco
      // A URL no banco deve ser sempre limpa, sem parâmetros de query
      const cleanAvatarUrl = urlData.publicUrl.split('?')[0];
      console.log('Updating profile with clean avatar URL (no cache-busting):', cleanAvatarUrl);

      // Atualiza o perfil no banco de dados com a URL limpa
      const updatedProfile = await updateProfile({
        avatar_url: cleanAvatarUrl
      });

      // O updateProfile já atualiza o estado local do perfil via setProfile
      // Não precisamos recarregar o perfil, pois isso causaria loops de atualização
      console.log('Profile updated successfully:', updatedProfile?.avatar_url);

      // Atualiza estado local com URL limpa
      setAvatarUrl(cleanAvatarUrl);

      // Atualiza avatar globalmente (vai adicionar cache-busting correto no frontend)
      // Isso vai sincronizar todos os componentes que usam o avatar
      updateGlobalAvatar(cleanAvatarUrl);

      toast.success("Imagem de perfil atualizada com sucesso!");
    } catch (error: any) {
      console.error("Erro completo:", error);
      toast.error(`Erro ao fazer upload da imagem: ${error.message}`);
    } finally {
      setUploading(false);
    }
  }

  async function removeAvatar() {
    try {
      setUploading(true);

      if (!avatarUrl) {
        console.log("No avatar URL to remove");
        return;
      }

      // Extract the file path from the URL with proper type checking
      const urlParts = avatarUrl.split('profiles/');
      if (urlParts.length > 1) {
        const filePath = urlParts[1]?.split('?')[0];

        if (filePath && typeof filePath === 'string') {
          console.log("Removing file:", filePath);
          // Remove o arquivo do storage
          const { error: removeError } = await supabase.storage
            .from('profiles')
            .remove([filePath]);

          if (removeError) {
            console.error("Error removing file:", removeError);
            // Continue with profile update even if file removal fails
          }
        }
      }

      // Update profile regardless of file removal success
      await updateProfile({ avatar_url: null });
      setAvatarUrl(null);

      // Update avatar globally to sync all components
      updateGlobalAvatar(null);

      toast.success("Profile image removed successfully!");
    } catch (error: any) {
      console.error("Error removing avatar:", error);
      toast.error(`Error removing image: ${error.message}`);
    } finally {
      setUploading(false);
    }
  }

  return {
    uploading,
    avatarUrl,
    uploadAvatar,
    removeAvatar
  };
}
